import { useState } from 'react';
import { api, ApiError, type ResolutionSpec } from '../lib/api';

/**
 * Configure a market to resolve itself, and check the rule before anyone
 * trades on it.
 *
 * The "Test this rule" button is the important control on this panel. The
 * alternative to it is discovering that the ticker was misspelled, or the
 * timezone was wrong, at settlement — in front of everyone who has money on the
 * outcome. Testing costs one request and takes a second, so there is no reason
 * to publish an untested rule.
 *
 * Leaving this off is always allowed. A manual market is not a lesser market;
 * most interesting questions have no API behind them.
 */
export function AutoResolve({
  spec,
  onChange,
}: {
  spec: ResolutionSpec | null;
  onChange: (spec: ResolutionSpec | null) => void;
}) {
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [testing, setTesting] = useState(false);

  const set = (patch: Record<string, unknown>) => {
    onChange({ ...(spec as object), ...patch } as ResolutionSpec);
    setPreview(null);
  };

  const test = async () => {
    if (!spec) return;
    setTesting(true);
    setPreview(null);
    try {
      setPreview(await api.previewResolution(spec));
    } catch (e) {
      setPreview({ ok: false, explanation: '', error: e instanceof ApiError ? e.message : 'Could not test the rule.' });
    } finally {
      setTesting(false);
    }
  };

  if (!spec) {
    return (
      <div className="field">
        <label>Resolution</label>
        <div className="hint" style={{ marginBottom: 8 }}>
          This market resolves manually — you decide the outcome and propose it yourself.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => onChange(defaultSpec('coinbase'))}>
            Resolve from a crypto price
          </button>
          <button type="button" onClick={() => onChange(defaultSpec('open-meteo'))}>
            Resolve from the weather
          </button>
          <button type="button" onClick={() => onChange(defaultSpec('equity-eod'))}>
            Resolve from a share price
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="field">
      <label>Resolution — automatic</label>

      {spec.source === 'coinbase' && (
        <Row>
          <Field label="Pair" width={130}>
            <input value={spec.product} onChange={(e) => set({ product: e.target.value })} placeholder="BTC-USD" />
          </Field>
          <Field label="At (UTC)" width={220}>
            <input
              type="datetime-local"
              value={spec.at.slice(0, 16)}
              onChange={(e) => set({ at: `${e.target.value}:00.000Z` })}
            />
          </Field>
          <Comparator spec={spec} set={set} />
          <Field label="Price" width={130}>
            <input
              type="number"
              value={String(spec.value)}
              onChange={(e) => set({ value: Number(e.target.value) })}
            />
          </Field>
        </Row>
      )}

      {spec.source === 'open-meteo' && (
        <>
          <Row>
            <Field label="Place" width={170}>
              <input value={spec.place ?? ''} onChange={(e) => set({ place: e.target.value })} placeholder="Prague" />
            </Field>
            <Field label="Latitude" width={110}>
              <input type="number" value={String(spec.latitude)} onChange={(e) => set({ latitude: Number(e.target.value) })} />
            </Field>
            <Field label="Longitude" width={110}>
              <input type="number" value={String(spec.longitude)} onChange={(e) => set({ longitude: Number(e.target.value) })} />
            </Field>
            <Field label="Timezone" width={170}>
              <input value={spec.timezone} onChange={(e) => set({ timezone: e.target.value })} placeholder="Europe/Prague" />
            </Field>
          </Row>
          <Row>
            <Field label="Measurement" width={210}>
              <select value={spec.variable} onChange={(e) => set({ variable: e.target.value })}>
                <option value="temperature_2m_max">Maximum temperature (°C)</option>
                <option value="temperature_2m_min">Minimum temperature (°C)</option>
                <option value="precipitation_sum">Total precipitation (mm)</option>
                <option value="rain_sum">Total rainfall (mm)</option>
                <option value="snowfall_sum">Total snowfall (cm)</option>
                <option value="wind_speed_10m_max">Maximum wind speed (km/h)</option>
              </select>
            </Field>
            <Field label="Day" width={160}>
              <input type="date" value={spec.date} onChange={(e) => set({ date: e.target.value })} />
            </Field>
            <Comparator spec={spec} set={set} />
            <Field label="Value" width={110}>
              <input type="number" value={String(spec.value)} onChange={(e) => set({ value: Number(e.target.value) })} />
            </Field>
          </Row>
        </>
      )}

      {spec.source === 'equity-eod' && (
        <>
          <Row>
            <Field label="Ticker" width={130}>
              <input value={spec.symbol} onChange={(e) => set({ symbol: e.target.value })} placeholder="AAPL" />
            </Field>
            <Field label="Session" width={160}>
              <input type="date" value={spec.date} onChange={(e) => set({ date: e.target.value })} />
            </Field>
            <Comparator spec={spec} set={set} />
            <Field label="Close" width={130}>
              <input type="number" value={String(spec.value)} onChange={(e) => set({ value: Number(e.target.value) })} />
            </Field>
          </Row>
          <div className="hint">
            Share prices are read the day after the session closes. Live prices are licensed and this
            exchange deliberately does not use them, so a market on today's close resolves tomorrow.
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <button type="button" onClick={test} disabled={testing}>
          {testing ? 'Checking the source…' : 'Test this rule'}
        </button>
        <button type="button" onClick={() => { onChange(null); setPreview(null); }}>
          Resolve this one manually instead
        </button>
      </div>

      {preview && (
        <div className={`notice ${preview.ok ? 'ok' : 'error'}`} style={{ marginTop: 12 }}>
          {preview.ok ? (
            <>
              <b>The source answered.</b> {preview.describe}
              <br />
              On today's reading this would resolve <b>{preview.wouldResolve}</b>.
              <div style={{ marginTop: 8, fontSize: 12.5, wordBreak: 'break-all' }}>
                <a href={preview.requestUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--yes)' }}>
                  {preview.requestUrl}
                </a>
              </div>
            </>
          ) : (
            <>
              <b>The source could not be read.</b> {preview.error}
              <div style={{ marginTop: 6, fontSize: 12.5 }}>
                A market with a rule that does not work will sit unresolved until you propose a
                result by hand. Fix it before publishing.
              </div>
            </>
          )}
        </div>
      )}

      {preview?.explanation && (
        <div className="hint" style={{ marginTop: 10 }}>
          Users will be told: “{preview.explanation}”
        </div>
      )}

      <div className="hint" style={{ marginTop: 10 }}>
        The resolver <b>proposes</b> a result — it never pays out on its own. Your dispute window
        still runs before anyone is paid, which is what protects you when a source publishes a
        correction.
      </div>
    </div>
  );
}

interface PreviewState {
  ok: boolean;
  explanation: string;
  describe?: string;
  requestUrl?: string;
  wouldResolve?: string;
  error?: string;
}

function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>{children}</div>;
}

function Field({ label, width, children }: { label: string; width: number; children: React.ReactNode }) {
  return (
    <div style={{ width }}>
      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 4, letterSpacing: '0.02em' }}>{label}</div>
      {children}
    </div>
  );
}

function Comparator({ spec, set }: { spec: ResolutionSpec; set: (p: Record<string, unknown>) => void }) {
  return (
    <Field label="Resolves YES if" width={150}>
      <select value={spec.comparison} onChange={(e) => set({ comparison: e.target.value })}>
        <option value="GTE">is at or above</option>
        <option value="GT">is above</option>
        <option value="LTE">is at or below</option>
        <option value="LT">is below</option>
        <option value="EQ">equals</option>
        <option value="NEQ">does not equal</option>
      </select>
    </Field>
  );
}

/** Sensible starting points, so the form is never empty and never invalid. */
function defaultSpec(source: ResolutionSpec['source']): ResolutionSpec {
  const tomorrow = new Date(Date.now() + 86_400_000);
  const day = tomorrow.toISOString().slice(0, 10);
  switch (source) {
    case 'coinbase':
      return {
        source: 'coinbase',
        product: 'BTC-USD',
        at: `${day}T16:00:00.000Z`,
        comparison: 'GTE',
        value: 100000,
      };
    case 'open-meteo':
      return {
        source: 'open-meteo',
        place: 'Prague',
        latitude: 50.0755,
        longitude: 14.4378,
        timezone: 'Europe/Prague',
        variable: 'temperature_2m_max',
        date: day,
        comparison: 'GTE',
        value: 25,
      };
    case 'equity-eod':
      return { source: 'equity-eod', symbol: 'AAPL', date: day, comparison: 'GT', value: 200 };
  }
}
